import React from 'react';
import PropTypes from 'prop-types';
import {
  Modal, Card, Table, Typography, Row, Col, Descriptions, Skeleton, Tooltip,
} from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { DEFAULT_DATE_TIME } from '../../../utils/constants/timeFormats';
import { StyledTable } from '../../../components';
import { estimatedEtaPropTypes } from '../../../types';

const EtaDetailsModal = ({ visible, onClose, vehicleEta }) => {
  const { t } = useTranslation();
  const { eta } = vehicleEta;
  const etaDetails = eta?.details;
  if (!etaDetails) {
    return null;
  }

  const formatDurationInSeconds = (duration, defaultValue) => {
    if (!duration && duration !== 0) return defaultValue;

    let result = '';
    const totalMinutes = Math.floor(duration / 60);
    const totalHours = Math.floor(totalMinutes / 60);

    const remainingMinutes = totalMinutes % 60;

    if (totalHours > 0) {
      result += `${totalHours}h `;
    }

    if (remainingMinutes > 0) {
      result += `${remainingMinutes}min`;
    }

    return result.trim();
  };

  const etaDetailsKey = 'ORDER.STOP.ETA.DETAILS';
  const etaInformationFieldKey = `${etaDetailsKey}.INFORMATION.FIELD`;
  const etaItineraryColumnsKey = `${etaDetailsKey}.ITINERARY.COLUMN`;

  const renderDefaultDriveCapacityValue = () => {
    if (!etaDetails.hasDrivingLimits) {
      return t(`${etaInformationFieldKey}.REMAINING_DRIVING_TIME_CAPACITY.NO_LIMIT`);
    }
    return (
      <Tooltip title={t('TACHOGRAPH.VIRTUAL.UNKNOWN.TOOLTIP_INFO')}>
        {`${t(`${etaInformationFieldKey}.REMAINING_DRIVING_TIME_CAPACITY.UNKNOWN`)} `}
        <InfoCircleOutlined />
      </Tooltip>
    );
  };

  const formatRemainingDriveTimeCapacity = (seconds) => formatDurationInSeconds(
    seconds, renderDefaultDriveCapacityValue(),
  );

  const viewLengthToEndInKm = (meters) => `${Math.floor(meters / 1000)}km left`;

  const renderEtaValue = () => (
    <div>
      <p style={{ margin: 0 }}>{moment.unix(eta.arrival).format(DEFAULT_DATE_TIME)}</p>
      <p style={{ margin: 0, opacity: 0.6 }}>
        {`${formatDurationInSeconds(eta.duration)}, ${viewLengthToEndInKm(eta.length)}`}
      </p>
    </div>
  );

  const renderRemainingDriveTimeCapacity = () => (
    <Row gutter={[8, 8]}>
      <Col span={24}>
        <Row>
          <Col span={8}>{t(`${etaInformationFieldKey}.REMAINING_DRIVING_TIME_CAPACITY.CURRENT`)}</Col>
          <Col span={16}>{formatRemainingDriveTimeCapacity(etaDetails.remainingDriveTimeCapacity.current)}</Col>
        </Row>
      </Col>
      <Col span={24}>
        <Row>
          <Col span={8}>{t(`${etaInformationFieldKey}.REMAINING_DRIVING_TIME_CAPACITY.DAILY`)}</Col>
          <Col span={16}>{formatRemainingDriveTimeCapacity(etaDetails.remainingDriveTimeCapacity.daily)}</Col>
        </Row>
      </Col>
      <Col span={24}>
        <Row>
          <Col span={8}>{t(`${etaInformationFieldKey}.REMAINING_DRIVING_TIME_CAPACITY.WEEKLY`)}</Col>
          <Col span={16}>{formatRemainingDriveTimeCapacity(etaDetails.remainingDriveTimeCapacity.weekly)}</Col>
        </Row>
      </Col>
    </Row>
  );

  const renderCalculationSource = () => {
    const sourceText = t(`${etaInformationFieldKey}.CALCULATION_SOURCE.VALUES.${etaDetails.source}`);
    return etaDetails.source === 'VIRTUAL_TACHO' ? (
      <Tooltip title={t('TACHOGRAPH.VIRTUAL.TOOLTIP_INFO')}>
        {`${sourceText} `}
        <InfoCircleOutlined />
      </Tooltip>
    ) : sourceText;
  };

  const etaInfoConfig = [
    {
      title: t(`${etaInformationFieldKey}.ETA_VALUE`),
      loading: () => false,
      renderDetail: () => renderEtaValue(),
    },
    {
      title: t(`${etaInformationFieldKey}.CURRENT_STATUS.NAME`),
      loading: () => false,
      renderDetail: () => t(`${etaInformationFieldKey}.CURRENT_STATUS.${etaDetails.state}`),
    },
    {
      title: t(`${etaInformationFieldKey}.REMAINING_DRIVING_TIME_CAPACITY.NAME`),
      loading: () => false,
      renderDetail: () => renderRemainingDriveTimeCapacity(),
    },
    {
      title: t(`${etaInformationFieldKey}.CALCULATION_SOURCE.NAME`),
      loading: () => false,
      renderDetail: () => renderCalculationSource(),
    },
  ];

  const itineraryColumns = [
    {
      title: t(`${etaItineraryColumnsKey}.SEQUENCE`),
      dataIndex: 'sequence',
      key: 'sequence',
    },
    {
      title: t(`${etaItineraryColumnsKey}.ACTIVITY.NAME`),
      dataIndex: 'activity',
      key: 'activity',
      render: (activity) => t(`${etaItineraryColumnsKey}.ACTIVITY.${activity}`),
    },
    {
      title: t(`${etaItineraryColumnsKey}.DESCRIPTION`),
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: t(`${etaItineraryColumnsKey}.START_TIME`),
      dataIndex: 'startTime',
      key: 'startTime',
    },
    {
      title: t(`${etaItineraryColumnsKey}.DURATION`),
      dataIndex: 'duration',
      key: 'duration',
      render: (duration) => formatDurationInSeconds(duration),
    },
  ];

  const itineraryData = etaDetails.itinerary?.map((item, index) => ({
    key: index,
    sequence: item.sequence || index + 1,
    activity: item.activity,
    description: item.description,
    startTime: item.startTime ? moment(item.startTime).format(DEFAULT_DATE_TIME) : null,
    duration: item.duration || 0,
  })) || [];

  return (
    <Modal
      title={(
        <h3>
          {t(`${etaDetailsKey}.MODAL.TITLE`, { licensePlateNumber: vehicleEta.licensePlateNumber })}
        </h3>
      )}
      visible={visible}
      onCancel={onClose}
      footer={null}
      destroyOnClose
      width={800}
    >
      <Card
        title={t(`${etaDetailsKey}.INFORMATION.TITLE`)}
        style={{ marginBottom: 16 }}
      >
        <Descriptions bordered size="small" column={1}>
          {etaInfoConfig.map((item) => (
            <Descriptions.Item label={t(item.title)} key={item.title}>
              {item.loading() ? <Skeleton.Input active size="small" /> : item.renderDetail()}
            </Descriptions.Item>
          ))}
        </Descriptions>
      </Card>

      <Card
        title={t(`${etaDetailsKey}.ITINERARY.TITLE`)}
        style={{ marginTop: 16 }}
      >
        {itineraryData.length > 0 ? (
          <StyledTable
            dataSource={itineraryData}
            columns={itineraryColumns}
            pagination={false}
            size="small"
            bordered
            summary={() => (
              <Table.Summary fixed>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>{t(`${etaDetailsKey}.ITINERARY.TOTAL`)}</Table.Summary.Cell>
                  <Table.Summary.Cell index={4}>{formatDurationInSeconds(etaDetails.totalTime)}</Table.Summary.Cell>
                </Table.Summary.Row>
              </Table.Summary>
            )}
          />
        ) : (
          <Typography.Text>{t(`${etaDetailsKey}.ITINERARY.NO_DATA`)}</Typography.Text>
        )}
      </Card>
    </Modal>
  );
};

EtaDetailsModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  vehicleEta: estimatedEtaPropTypes.isRequired,
};

export default EtaDetailsModal;
